import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ListingOverview } from "@/types/listing";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import { playListingHeroTransition } from "@/lib/native/listingHeroTransition";
import { buildAuctionListingPath } from "@/lib/listingUrl";

type NavigateListingOptions = {
  from?: string;
  fromScrollY?: number;
  heroImageEl?: HTMLElement | null;
  thumbnailUrl?: string;
};

/** Resolve hero image container from a card click target. */
export function getListingHeroFromClick(
  event: MouseEvent<HTMLElement>,
): HTMLElement | null {
  const root = event.currentTarget as HTMLElement;
  const hero = root.querySelector("[data-listing-hero-image]");
  return hero instanceof HTMLElement ? hero : null;
}

/** Navigate to listing detail with native hero expand when possible. */
export async function navigateToListingDetail(
  navigate: ReturnType<typeof useNavigate>,
  listing: Pick<ListingOverview, "id" | "slug" | "campaignId" | "thumbnail">,
  options?: NavigateListingOptions,
) {
  const state = {
    from: options?.from ?? "/auctions",
    fromScrollY: options?.fromScrollY ?? window.scrollY,
    listingTransitionId: listing.id,
  };

  const path = buildAuctionListingPath(listing.campaignId, listing);

  const go = () => navigate(path, { state });

  const thumbnailUrl = options?.thumbnailUrl ?? listing.thumbnail;
  const canHero =
    isNativeCapacitorApp() &&
    options?.heroImageEl &&
    thumbnailUrl;

  if (canHero) {
    await playListingHeroTransition(
      options.heroImageEl!,
      thumbnailUrl,
      go,
    );
    return;
  }

  if (
    typeof document !== "undefined" &&
    "startViewTransition" in document &&
    typeof (document as Document & { startViewTransition?: (cb: () => void) => void })
      .startViewTransition === "function"
  ) {
    (
      document as Document & { startViewTransition: (cb: () => void) => void }
    ).startViewTransition(go);
    return;
  }

  go();
}

/** Convenience: hero transition from any listing card click. */
export async function navigateToListingFromClick(
  navigate: ReturnType<typeof useNavigate>,
  listing: Pick<ListingOverview, "id" | "slug" | "campaignId" | "thumbnail">,
  event: MouseEvent<HTMLElement>,
  options?: Omit<NavigateListingOptions, "heroImageEl" | "thumbnailUrl">,
) {
  return navigateToListingDetail(navigate, listing, {
    ...options,
    heroImageEl: getListingHeroFromClick(event),
    thumbnailUrl: listing.thumbnail,
  });
}

export function listingCardTransitionName(listingId: string): string {
  return `listing-card-${listingId}`;
}
